/**
 * The one stylesheet.
 *
 * Emitted as a string rather than a `.css` file so that the printed document
 * and the on-screen preview are styled by provably the same bytes.
 *
 * THE SEVEN RULES THAT PRODUCE ALIGNED BORDERS
 * ---------------------------------------------
 * 1. `table-layout: fixed` - widths come only from `<col>`. Content can NEVER
 *    widen a column, so a cell's content width and its border position can
 *    never disagree. The most important line in the file.
 * 2. `border-collapse: collapse` - adjacent borders merge into one shared
 *    stroke. `border-spacing` gives two hairlines and a gap, which reads as a
 *    blurred, doubled rule.
 * 3. ONE border-width token. 1.6.x used `!important` overrides that changed
 *    border width and padding BETWEEN the print and PDF paths, so neighbouring
 *    cells literally had different border thicknesses.
 * 4. ONE font declaration, from ONE place. 1.6.x loaded Source Serif from a
 *    CDN and fell back to Georgia offline; different glyph metrics means
 *    different string widths, which is the actual mechanism behind text
 *    escaping its border. The family, the two files and the load check all
 *    live in `font.ts` so this file cannot drift from the thermal one.
 * 5. ZERO runtime style mutation. 1.6.x wrote `overflow-wrap: anywhere` onto
 *    address lines during a 1px layout and printed 750ms later. Nothing here
 *    may be measured and rewritten after paint.
 * 6. `overflow-wrap: normal` by default, and wrapping enabled only by an
 *    explicit class - so a long token cannot silently re-flow a fixed grid.
 * 7. The totals block is a `<table>` on the same millimetre grid, not a CSS
 *    Grid. A grid with a hand-tuned px floor is what drifted away from the
 *    item table's right edge in 1.6.x.
 *
 * NO UTILITY CLASSES, NO TAILWIND - banned from `core/doc/` by ESLint Rule 3,
 * and rightly: a tenth styling authority is exactly how two authorities come
 * to disagree.
 */
import { A4_HEIGHT_MM, A4_WIDTH_MM, PAGE_MARGIN_MM, PRINTABLE_WIDTH_MM } from './geometry';
import { docFontFacesCss, docFontFamilyCss } from './font';
import { MODE_ACCENT_COLOR } from './renderMode';

const _ACCENT_RULES = Object.entries(MODE_ACCENT_COLOR)
  .map(([mode, color]) => `  .doc-mode-${mode} { --doc-accent-color: ${color}; }`)
  .join('\n');

/**
 * @param assetsBaseUrl directory the two TTFs are served from, WITH a trailing
 *        slash. Must actually resolve - see `assertDocFontsLoaded`, which the
 *        print and PDF paths call before printing.
 */
export function a4Stylesheet(assetsBaseUrl = ''): string {
  return `
${docFontFacesCss(assetsBaseUrl)}

:root {
  --doc-border-width: 0.2mm;
  --doc-border: var(--doc-border-width) solid #000;
  /* A heavier rule for structural divisions only: the title underline, the
     grand total. Never used for ordinary cell borders, which is how a table
     stops looking like a spreadsheet. */
  --doc-rule-width: 0.5mm;
  --doc-rule: var(--doc-rule-width) solid #000;
  --doc-pad-y: 1mm;
  --doc-pad-x: 1.2mm;
  /* The one tint used for emphasis. Deliberately a single value: a document
     that tints three things differently reads as decoration, and printers
     reproduce those tints unreliably anyway. */
   --doc-tint: #eef1f5;
  --doc-muted: #555;
}
${_ACCENT_RULES}

* { box-sizing: border-box; }

html, body {
  margin: 0;
  padding: 0;
  background: #fff;
  color: #000;
}

/* Rule 4: one declared family plus a DOCUMENTED last-resort term. Substitution
   is not preventable in CSS - it is prevented by the assertDocFontsLoaded()
   function, which the print and PDF paths call before printing. See font.ts. */
body {
  ${docFontFamilyCss()}
  /* 9pt, up from 8.5pt. The old size was chosen for the item table alone; once
     the type carries more hierarchy it also has to carry the address blocks
     and the totals, and 8.5pt on those read as fine print. Every width that
     matters is set in millimetres, so this moves no border. */
  font-size: 9pt;
  line-height: 1.3;
}

.doc-page {
  width: ${A4_WIDTH_MM}mm;
  min-height: ${A4_HEIGHT_MM}mm;
  padding: ${PAGE_MARGIN_MM}mm;
  margin: 0 auto;
  background: #fff;
}

/* Rule 1: fixed layout. The whole file depends on this line. */
table.doc-table {
  width: ${PRINTABLE_WIDTH_MM}mm;
  table-layout: fixed;
  border-collapse: collapse;            /* Rule 2 */
  /* NO table-level border. With border-collapse:collapse the collapsed border
     is drawn half OUTSIDE the table box, so declaring one here made the table
     measure 719.05px against a 190mm (718.11px) target and pushed its right
     edge a full pixel past the totals block's. The outermost cells already
     draw the box; a second declaration would only double it. */
  margin: 0 0 2mm 0;
}

table.doc-table > thead > tr > th,
table.doc-table > tbody > tr > td,
table.doc-table > tfoot > tr > td {
  border: var(--doc-border);           /* Rule 3: one token, everywhere */
  padding: var(--doc-pad-y) var(--doc-pad-x);
  vertical-align: top;
  text-align: left;
  font-weight: 400;
  /* Rule 6: wrapping is chosen by class, never guessed by the browser. */
  overflow-wrap: normal;
  word-break: normal;
}

.doc-table thead th {
  font-weight: 700;
  text-align: center;
  /* The header band is the item table's single tint. */
  background: var(--doc-tint);
  /* A taller header row: it is the row a reader's eye returns to, and at 1mm
     padding it had the same density as the data rows it heads. */
  padding-top: 1.4mm;
  padding-bottom: 1.4mm;
}

/* Zebra striping on alternate body rows.
   Purely for scannability: a 40-line invoice has no visual cue for which row a
   line ends on, and that is what makes a long table read as noise. Kept very
   light so it survives a greyscale laser print without turning the page grey. */
table.doc-table > tbody > tr:nth-child(even) > td { background: #f7f8fa; }

.doc-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.doc-ctr { text-align: center; white-space: nowrap; }
/* Wrapping must be able to OVERRIDE the rule above, and it could not.
   The base cell rule (table.doc-table > tbody > tr > td) is (0,1,4) - one
   class plus four elements - while a bare .doc-wrap is only (0,1,0). The base
   rule's overflow-wrap: normal therefore won every time, so the description
   cell never wrapped and a long unbroken token spilled 766px out of its
   column and over the printed page edge.

   The test suite missed it because the geometry test measured the TABLE's
   width. table-layout: fixed pins that to 190mm no matter what the cells
   contain, so the measurement stayed green while the ink did not. A per-cell
   scrollWidth > clientWidth check is what actually sees it.

   Same element chain as the base rule on purpose: that makes the override win
   by specificity, so it does not depend on source order.

   NOTE: no backticks anywhere in this comment - the stylesheet is a template
   literal, so a backtick here silently terminates the string. */
table.doc-table > thead > tr > th.doc-wrap,
table.doc-table > tbody > tr > td.doc-wrap,
table.doc-table > tfoot > tr > td.doc-wrap {
  overflow-wrap: break-word;
  word-break: break-word;
}
/* The business logo, in the seller column.
   Sized in MILLIMETRES on purpose. A percentage or auto height makes the
   letterhead's height depend on the image's pixel dimensions, which makes page
   breaks depend on which logo a business happens to have uploaded - so the same
   invoice paginates differently for two shops, and the geometry tests stop
   meaning anything. A fixed box cannot do that. */
.doc-logo {
  display: block;
  max-width: 38mm;
  max-height: 16mm;
  width: auto;
  height: auto;
  object-fit: contain;
  /* Not centred. A logo belongs to the business, and left-aligned over the legal
     name reads as a letterhead; centred under the name reads as a stray icon. */
  margin: 0 0 2mm 0;
}

/* The signature, between "For <company>" and "Authorised Signatory".
   Wider than it is tall, because that is the shape of a signature, and
   constrained so a photo of a signature cannot become a portrait on the page. */
.doc-signature {
  display: block;
  max-width: 34mm;
  max-height: 12mm;
  width: auto;
  height: auto;
  object-fit: contain;
}

.doc-strong { font-weight: 700; }

/* ?? Page breaks ??????????????????????????????????????????????????????????
   THE MOST IMPORTANT THING THIS BLOCK DOES IS PREVENT THE HEADER SPLITTING.
   A long invoice breaks across sheets, and before this rule the boundary could
   land anywhere - including between the letterhead and the item table, or
   inside the grand total. Both are visible defects on a document a customer
   reads.

   A single-line item cannot be split so that its description prints on one
   sheet and its amount on the next.
   Browsers honour these without script, which matters: Rule 5 forbids
   measuring and rewriting the document after paint, so pagination control has
   to live in the stylesheet or nowhere. */
thead { display: table-header-group; }
tr, .doc-keep { break-inside: avoid; page-break-inside: avoid; }
thead { break-after: avoid; page-break-after: avoid; }
/* The letterhead, the totals and the summary tables each stay whole. */
.doc-head, .doc-totals, .doc-summary, .doc-tnc-block {
  break-inside: avoid;
  page-break-inside: avoid;
}

/* ?? Letterhead ???????????????????????????????????????????????????????????
   Three zones: who is selling, what the document is, and who is buying. The
   old header put the seller top-left and the BUYER bottom-right with an empty
   cell opposite, so the two looked unrelated and neither was labelled. The two
   party blocks are now side by side, each captioned. */
.doc-head { width: ${PRINTABLE_WIDTH_MM}mm; margin-bottom: 2.5mm; }
.doc-head-row { width: ${PRINTABLE_WIDTH_MM}mm; border: none; table-layout: fixed; }
.doc-head-row > td { border: none; padding: 0; vertical-align: top; }

/* Seller and buyer, 62/38. The seller needs more room: it carries the address,
   the GSTIN and the contact line, and a buyer usually needs only a name, a
   GSTIN and two address lines. */
table.doc-letterhead { width: ${PRINTABLE_WIDTH_MM}mm; table-layout: fixed; border-collapse: collapse; }
table.doc-letterhead > tbody > tr > td { border: none; padding: 0 3mm 0 0; vertical-align: top; }

.doc-biz-name { font-size: 11pt; font-weight: 700; line-height: 1.2; }
.doc-biz-line { font-size: 8pt; color: var(--doc-muted); }
.doc-gstin { font-weight: 700; }

/* The document title, above the buyer block. */
.doc-title {
  font-size: 13pt;
  font-weight: 700;
  color: var(--doc-accent-color);
  letter-spacing: 0.4mm;
  text-transform: uppercase;
  margin: 0 0 1mm 0;
  /* A rule under the title rather than a box around it: the title should read
     as the head of the document, not as a button. */
  border-bottom: var(--doc-rule);
  padding-bottom: 1mm;
}

/* A section caption - BILL TO, SHIP TO, GST BREAKUP. */
.doc-caption {
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0.3mm;
  text-transform: uppercase;
  color: var(--doc-muted);
  margin: 0 0 0.8mm 0;
}

/* The document-number / date / place-of-supply block. It is NOT a full-width
   table: it lives in one column of the letterhead, and giving it the
   .doc-table class made it demand the whole 190mm and push the totals block's
   right edge off the page. */
table.doc-meta { width: 100%; table-layout: fixed; border-collapse: collapse; }
table.doc-meta > tbody > tr > td { border: none; padding: 0.5mm 0; vertical-align: top; }
table.doc-meta td.doc-label-cell { width: 34mm; font-weight: 700; }
table.doc-meta td.doc-value-cell { text-align: right; font-variant-numeric: tabular-nums; }

/* Rule 7: the totals block is a table on the same grid, not a CSS Grid. */
table.doc-totals {
  width: ${PRINTABLE_WIDTH_MM}mm;
  table-layout: fixed;
  border-collapse: collapse;
  margin-top: 2mm;
}
table.doc-totals > tbody > tr > td {
  border: none;
  padding: 0.7mm var(--doc-pad-x);
  vertical-align: top;
}
table.doc-totals td.doc-total-label { text-align: left; }
table.doc-totals td.doc-total-value {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* A muted intermediate row: a real figure the reader scans past on the way to
   the grand total. Indented and greyed so it does not compete with it. */
table.doc-totals tr.doc-sub > td {
  color: var(--doc-muted);
  padding-top: 0.5mm;
  padding-bottom: 0.5mm;
}

/* THE GRAND TOTAL. This is the one number the reader came for, so it is the one
   place a heavier rule and a tint are spent. Bordered top and bottom only: a
   full box around it competes with the item table it belongs to. */
table.doc-totals tr.doc-grand > td {
  border-top: var(--doc-rule);
  border-bottom: var(--doc-rule);
  background: var(--doc-tint);
  font-weight: 700;
  font-size: 10.5pt;
  padding-top: 1.4mm;
  padding-bottom: 1.4mm;
}
/* Credit Note: red grand total */
.doc-mode-credit-note table.doc-totals tr.doc-grand > td:last-child {
  color: var(--doc-accent-color);
}
/* Debit Note: orange grand total */
.doc-mode-debit-note table.doc-totals tr.doc-grand > td:last-child {
  color: var(--doc-accent-color);
}
/* Sales Return: blue grand total */
.doc-mode-sales-return table.doc-totals tr.doc-grand > td:last-child {
  color: var(--doc-accent-color);
}
/* Purchase Return: purple grand total */
.doc-mode-purchase-return table.doc-totals tr.doc-grand > td:last-child {
  color: var(--doc-accent-color);
}

/* Amount in words sits directly above the grand total, because it is the same
   number said twice and reading the two together is the point. */
.doc-words {
  margin: 1.5mm 0 0 0;
  padding: 1.2mm var(--doc-pad-x);
  background: var(--doc-tint);
  font-size: 8.5pt;
}
.doc-words .doc-label { font-weight: 700; }

/* ?? Summary tables (GST breakup / HSN summary) ??????????????????????????? */
table.doc-summary {
  width: ${PRINTABLE_WIDTH_MM}mm;
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0;
}
table.doc-summary > thead > tr > th,
table.doc-summary > tbody > tr > td {
  border: var(--doc-border);
  padding: var(--doc-pad-y) var(--doc-pad-x);
  vertical-align: top;
  text-align: left;
  font-weight: 400;
  overflow-wrap: normal;
  word-break: normal;
}
table.doc-summary > thead > tr > th {
  font-weight: 700;
  text-align: center;
  background: var(--doc-tint);
}
table.doc-summary td.doc-num, table.doc-summary th.doc-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.doc-summary-block { margin-top: 2.5mm; break-inside: avoid; page-break-inside: avoid; }
.doc-summary-title { font-weight: 700; margin: 0 0 1mm 0; font-size: 8.5pt; }

/* A DRAFT banner.
   Only ever reached for a document whose status is DRAFT. It exists because
   The status field was already in the render model and never printed, which
   meant a draft was pixel-identical to an issued invoice and could be sent to a
   customer and filed. Bordered rather than tinted, because it is the one
   element that must survive a greyscale print. */
.doc-draft-banner {
  margin: 0 0 2.5mm 0;
  padding: 1.2mm 2mm;
  border: var(--doc-rule);
  font-weight: 700;
  font-size: 9pt;
  letter-spacing: 0.3mm;
  text-transform: uppercase;
  text-align: center;
}

/* A second description line under the item name. */
.doc-line-extra { color: var(--doc-muted); font-size: 8pt; }
/* Product name in the description cell - primary label. */
.doc-line-product { font-weight: 600; font-size: 9pt; }
/* User-entered description in the description cell - subtitle. */
.doc-line-description { color: var(--doc-muted); font-size: 8pt; margin-top: 0.5mm; }
/* The tax AMOUNT under its rate, within the GST cell. */
.doc-line-tax { font-weight: 700; }

/* ?? Footer ???????????????????????????????????????????????????????????????
   Terms, notes and declaration are read from the DOCUMENT, never from the
   business profile: an issued invoice is a historical record, and re-reading
   the profile at print time would silently rewrite it. Each block is emitted
   only when it has content, so an invoice with no terms prints no empty
   "Terms:" label. */
.doc-foot { margin-top: 2.5mm; font-size: 8pt; }
.doc-foot .doc-label { font-weight: 700; }
.doc-foot-block { margin-top: 2.5mm; font-size: 8pt; }
.doc-foot-block > .doc-label { display: block; font-weight: 700; margin-bottom: 0.4mm; }

/* Bank details and the signature share one row, so the signatory sits at the
   foot of the page rather than wherever the bank block happened to end. */
table.doc-sign {
  width: ${PRINTABLE_WIDTH_MM}mm;
  table-layout: fixed;
  border-collapse: collapse;
  margin-top: 6mm;
}
table.doc-sign > tbody > tr > td { border: none; padding-top: 7mm; vertical-align: top; }
table.doc-sign td.doc-sign-right { text-align: right; }
.doc-computed { margin-top: 4mm; font-size: 7.5pt; color: var(--doc-muted); text-align: center; }

@media print {
  /*
   * The @page margin is ZERO, and that is a fix rather than a style choice.
   *
   * .doc-page is already a full A4 sheet (210 x 297mm) that pads itself
   * internally by PAGE_MARGIN_MM. Giving @page the SAME margin as well applies
   * it twice: the printable area shrinks to 210-2M by 297-2M while the page box
   * stays 210 by 297, so every invoice overflows its printable area and Chromium
   * emits a second, essentially BLANK sheet.
   *
   * That is invisible on screen - the preview is the page box, not the printable
   * area - and invisible to every DOM assertion, because there is one document
   * either way. It is only real in the PDF a customer receives.
   *
   * The page box keeps its own padding, which is also what makes the printed
   * page and the on-screen preview the same picture.
   */
  @page { size: A4 portrait; margin: 0; }
  html, body { width: ${A4_WIDTH_MM}mm; }
  .doc-page { margin: 0; }
  /* Rule 5, enforced: print must not restyle anything. */
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}

/* ?? Phase 1: per-mode structural elements ????????????????????????????????????
   Scoped to doc-mode parent selectors so they only apply to their own document
   type. Every rule is ADDITIVE ? none overrides an existing doc- class or
   changes the geometry invariant. */

/* A PROFORMA notice banner: bold, tinted, unmistakable. Styled like the draft
   banner but tinted by the proforma accent colour ? the only visual variant
   spent on a non-data element, and only because the non-invoice status must be
   seen in a pile of papers. */
.doc-mode-proforma .doc-proforma-banner {
  margin: 0 0 2.5mm 0;
  padding: 2mm 3mm;
  border: var(--doc-rule);
  border-left-width: 4mm;
  border-left-style: solid;
  border-left-color: var(--doc-accent-color);
  background: var(--doc-tint);
  text-align: center;
}
.doc-mode-proforma .doc-proforma-banner-title {
  font-weight: 700;
  font-size: 10pt;
  letter-spacing: 0.4mm;
  text-transform: uppercase;
  margin-bottom: 1mm;
}
.doc-mode-proforma .doc-proforma-banner-text {
  font-size: 8pt;
  color: var(--doc-muted);
  font-style: italic;
}
.doc-mode-proforma .doc-page {
  position: relative;
}
.doc-mode-proforma .doc-page::before {
  content: 'ESTIMATE';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  font-size: 72pt;
  font-weight: 900;
  color: rgba(202, 138, 4, 0.06);
  white-space: nowrap;
  pointer-events: none;
  z-index: 0;
}
.doc-mode-proforma .doc-proforma-notice {
  margin: 0 0 2.5mm 0;
  padding: 1.5mm 2mm;
  border: var(--doc-rule);
  border-left-width: 4mm;
  border-left-style: solid;
  border-left-color: var(--doc-accent-color);
  font-weight: 700;
  font-size: 8.5pt;
  letter-spacing: 0.3mm;
  text-transform: uppercase;
  text-align: center;
  background: var(--doc-tint);
}

/* Receiver acknowledgement block for a DELIVERY_NOTE.
   Two labelled lines with a ruled space beneath each ? filled in by hand. */
.doc-mode-delivery-note .doc-ack {
  margin-top: 4mm;
}
.doc-mode-delivery-note .doc-ack-table {
  width: ${PRINTABLE_WIDTH_MM}mm;
  table-layout: fixed;
  border-collapse: collapse;
}
.doc-mode-delivery-note .doc-ack-table td {
  border: none;
  padding: 0;
  vertical-align: top;
}
.doc-mode-delivery-note .doc-ack-label {
  font-weight: 700;
  font-size: 8pt;
  margin-bottom: 1mm;
  display: block;
}
.doc-mode-delivery-note .doc-ack-line {
  border-bottom: var(--doc-border);
  width: 90%;
  margin-bottom: 1mm;
}
.doc-mode-credit-note .doc-correction-notice {
  margin: 0 0 2.5mm 0;
}
.doc-mode-credit-note .doc-credit-note {
  margin: 0 0 2.5mm 0;
  padding: 1.5mm 2mm;
  border: var(--doc-rule);
  border-left-width: 4mm;
  border-left-style: solid;
  border-left-color: var(--doc-accent-color);
  font-size: 8pt;
  font-style: italic;
  color: var(--doc-muted);
  text-align: center;
  background: var(--doc-tint);
}
}
.doc-mode-debit-note .doc-correction-notice {
  margin: 0 0 2.5mm 0;
}
.doc-mode-purchase-return .doc-correction-notice {
  margin: 0 0 2.5mm 0;
  padding: 1.5mm 2mm;
  border: var(--doc-rule);
  border-left-width: 4mm;
  border-left-style: solid;
  border-left-color: var(--doc-accent-color);
  font-weight: 600;
  font-size: 8.5pt;
  text-align: center;
  background: var(--doc-tint);
}
  padding: 1.5mm 2mm;
  border: var(--doc-rule);
  border-left-width: 4mm;
  border-left-style: solid;
  border-left-color: var(--doc-accent-color);
  font-weight: 600;
  font-size: 8.5pt;
  text-align: center;
  background: var(--doc-tint);
}
.doc-mode-debit-note .doc-ack-caps {
  font-size: 7.5pt;
  padding: 1.5mm 2mm;
  border: var(--doc-rule);
  border-left-width: 4mm;
  border-left-style: solid;
  border-left-color: var(--doc-accent-color);
  font-weight: 600;
  font-size: 8.5pt;
  text-align: center;
  background: var(--doc-tint);
}
.doc-mode-delivery-note .doc-ack-caps {
  font-size: 7.5pt;
  color: var(--doc-muted);
  font-variant-small-caps: all;
}
.doc-mode-delivery-note .doc-ack-check {
  margin-bottom: 3mm;
  display: flex;
  align-items: center;
  gap: 2mm;
}
.doc-mode-delivery-note .doc-ack-checkbox {
  font-size: 12pt;
  line-height: 1;
}
.doc-mode-delivery-note .doc-ack-checklabel {
  font-size: 8.5pt;
  font-weight: 600;
}
.doc-mode-delivery-note .doc-ack-total {
  margin-top: 3mm;
  font-size: 8.5pt;
  font-weight: 600;
  text-align: right;
}
.doc-mode-delivery-note .doc-ack-total-label {
  font-weight: 700;
}
.doc-mode-delivery-note .doc-ack-total-value {
  font-variant-numeric: tabular-nums;
  margin-left: 2mm;
}
.doc-mode-purchase-order .doc-delivery-instructions {
   margin: 0 0 2.5mm 0;
   padding: 1.5mm 2mm;
   border: var(--doc-rule);
   font-size: 8.5pt;
   background: var(--doc-tint);
}
/* ?? Purchase Order: distinct procure-to-pay visual hierarchy ???
   These rules are ADDITIVE and scoped to doc-mode-purchase-order only.
   They create a structurally different header, panels, info strip, and
   terms section that must NOT alter any other document type. */

.doc-mode-purchase-order .doc-po-head {
  margin-bottom: 3mm;
}
.doc-mode-purchase-order .doc-po-bar {
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-bottom: 3mm;
}
.doc-mode-purchase-order .doc-po-bar > div {
  display: table-cell;
  border: none;
  padding: 0;
  vertical-align: top;
}
.doc-mode-purchase-order .doc-po-bar-left {
  width: 62%;
}
.doc-mode-purchase-order .doc-po-bar-right {
  width: 38%;
  text-align: right;
}
.doc-mode-purchase-order .doc-po-business {
  display: inline-block;
}
.doc-mode-purchase-order .doc-po-docmeta {
  display: inline-block;
  text-align: right;
}
.doc-mode-purchase-order .doc-title {
  margin: 0 0 1mm 0;
  padding-bottom: 1mm;
}
.doc-mode-purchase-order .doc-po-number {
  display: block;
  font-weight: 700;
  font-size: 9pt;
  color: var(--doc-muted);
  margin: 0;
}
.doc-mode-purchase-order .doc-po-date {
  font-size: 8.5pt;
  color: var(--doc-muted);
}

.doc-mode-purchase-order .doc-po-panels {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-bottom: 2.5mm;
}
.doc-mode-purchase-order .doc-po-panel {
  border: var(--doc-border);
  padding: 2mm;
  vertical-align: top;
  font-size: 9pt;
}
.doc-mode-purchase-order .doc-po-from { border-right: none; }
.doc-mode-purchase-order .doc-po-to { border-left: none; }
.doc-mode-purchase-order .doc-po-panel .doc-caption {
  margin-bottom: 1.5mm;
}

.doc-mode-purchase-order .doc-po-info-strip {
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-bottom: 2.5mm;
  font-size: 8.5pt;
}
.doc-mode-purchase-order .doc-po-info-item {
  display: table-cell;
  border: var(--doc-border);
  padding: 0.8mm 1.5mm;
  vertical-align: middle;
}
.doc-mode-purchase-order .doc-po-info-label {
  font-weight: 700;
  color: var(--doc-muted);
  margin-right: 2mm;
}

.doc-mode-purchase-order .doc-po-delivery {
  margin: 0 0 2.5mm 0;
  padding: 1.5mm 2mm;
  border: var(--doc-rule);
  font-size: 8.5pt;
  background: var(--doc-tint);
}
.doc-mode-purchase-order .doc-po-delivery .doc-label {
  font-weight: 700;
}

.doc-mode-purchase-order .doc-tnc-block {
  margin: 0 0 3mm 0;
  padding: 1.5mm 2mm 1.5mm 6mm;
  border-left: 4mm solid var(--doc-accent-color);
  background: var(--doc-tint);
  font-size: 8.5pt;
}
.doc-mode-purchase-order .doc-tnc-title {
  font-weight: 700;
  font-size: 9.5pt;
  margin: 0 0 2mm 0;
  color: var(--doc-muted);
}
.doc-mode-purchase-order .doc-tnc-item {
  margin-bottom: 1mm;
  line-height: 1.4;
}

/* Draft banner inside the PO bar sits to the right, below the doc meta */
.doc-mode-purchase-order .doc-draft-banner {
  margin-top: 1.5mm;
}
`.trim();
}
